import { point } from '../../../elements/point';
import { ICON_DEFAULT_FOREGROUND_COLOR } from '../../../graphConstants';
import {
  ARC_LARGE_LEFT,
  ARC_LARGE_RIGHT,
  SvgPathBuilder,
} from '../../../svg/SvgPathBuilder';
import { makeTag } from '../../../svg/utils';
import { squared } from '../../../utils/numbers';
import {
  type NodeIconOptions,
  type NodeTypeIcon,
  nodeIconDefaultOptions,
} from '../NodeTypeIcon';
import {
  createSquareNodeTypeIcon,
  getArrowSvgPath,
  wrapNodeIconInSvgTag,
} from '../common';
import { drawSquareBackground } from '../drawIconBackground';

const ICON_SIZE = 40;
const RING_CY = 33.2;
const RING_GAP_SIZE = 1.6;
const RING_RX = 3.2;
const RING_RY = 2.4;
const RING_THICKNESS = 1.2;
const MINOR_ARROW_TOP = 18;
const MINOR_ARROW_BOTTOM = 28;
const MINOR_ARROW_BOTTOM_SIDE = 13.6;
const MINOR_ARROW_TOP_SIDE = 6.8;

const RING_GAP_TOP = RING_CY - RING_GAP_SIZE / 2;
const RING_GAP_BOTTOM = RING_CY + RING_GAP_SIZE / 2;

const RING_OUTER_RX = RING_RX + RING_THICKNESS / 2;
const RING_OUTER_RY = RING_RY + RING_THICKNESS / 2;
const RING_INNER_RX = RING_RX - RING_THICKNESS / 2;
const RING_INNER_RY = RING_RY - RING_THICKNESS / 2;

function createWlcIcon(options: NodeIconOptions = {}): string {
  const opts = { ...nodeIconDefaultOptions, ...options };

  const ringX = (offset: number) => 20 + offset * 6.8;
  const drawRing = (offset: number) =>
    makeTag('ellipse', {
      cx: ringX(offset),
      cy: RING_CY,
      rx: RING_RX,
      ry: RING_RY,
      fill: 'none',
      stroke: ICON_DEFAULT_FOREGROUND_COLOR,
      'stroke-width': RING_THICKNESS,
    });

  const getEllipsisX = (y: number, rx: number, ry: number) =>
    rx * Math.sqrt(1 - squared(y) / squared(ry));

  const getOuterEllipsisX = (y: number) =>
    getEllipsisX(y, RING_OUTER_RX, RING_OUTER_RY);
  const getInnerEllipsisX = (y: number) =>
    getEllipsisX(y, RING_INNER_RX, RING_INNER_RY);

  const gapLeftOuterX = ringX(-2) - getOuterEllipsisX(RING_GAP_SIZE / 2);
  const gapLeftInnerX = ringX(-2) - getInnerEllipsisX(RING_GAP_SIZE / 2);
  const gapRightInnerX = ringX(2) + getInnerEllipsisX(RING_GAP_SIZE / 2);
  const gapRightOuterX = ringX(2) + getOuterEllipsisX(RING_GAP_SIZE / 2);

  const leftOpenRing = makeTag('path', {
    d: new SvgPathBuilder()
      .moveTo(gapLeftOuterX, RING_GAP_TOP)
      .arcTo(
        RING_OUTER_RX,
        RING_OUTER_RY,
        0,
        ARC_LARGE_LEFT,
      )(gapLeftOuterX, RING_GAP_BOTTOM)
      .hLineTo(gapLeftInnerX)
      .arcTo(
        RING_INNER_RX,
        RING_INNER_RY,
        0,
        ARC_LARGE_RIGHT,
      )(gapLeftInnerX, RING_GAP_TOP)
      .buildClosed(),
    fill: ICON_DEFAULT_FOREGROUND_COLOR,
  });

  const rightOpenRing = makeTag('path', {
    d: new SvgPathBuilder()
      .moveTo(gapRightOuterX, RING_GAP_TOP)
      .arcTo(
        RING_OUTER_RX,
        RING_OUTER_RY,
        0,
        ARC_LARGE_RIGHT,
      )(gapRightOuterX, RING_GAP_BOTTOM)
      .hLineTo(gapRightInnerX)
      .arcTo(
        RING_INNER_RX,
        RING_INNER_RY,
        0,
        ARC_LARGE_LEFT,
      )(gapRightInnerX, RING_GAP_TOP)
      .buildClosed(),
    fill: ICON_DEFAULT_FOREGROUND_COLOR,
  });

  const mainArrow = makeTag('path', {
    d: getArrowSvgPath({
      from: point.create(20, 16),
      to: point.create(20, 2.4),
      headWidth: 9.6,
      headHeight: 9.6,
      headSkew: 0.8,
      tailWidth: 3.6,
    }),
    fill: ICON_DEFAULT_FOREGROUND_COLOR,
  });

  const getMinorArrowSvg = (fromX: number, toX: number) =>
    makeTag('path', {
      d: getArrowSvgPath({
        from: point.create(fromX, MINOR_ARROW_BOTTOM),
        to: point.create(toX, MINOR_ARROW_TOP),
        headWidth: 6,
        headHeight: 6,
        headSkew: 0.4,
        tailWidth: 1.6,
      }),
      fill: ICON_DEFAULT_FOREGROUND_COLOR,
    });

  const icon = `
    ${drawSquareBackground(ICON_SIZE, opts.style)}
    ${mainArrow}
    ${getMinorArrowSvg(20 - MINOR_ARROW_BOTTOM_SIDE, 20 - MINOR_ARROW_TOP_SIDE)}
    ${getMinorArrowSvg(20, 20)}
    ${getMinorArrowSvg(20 + MINOR_ARROW_BOTTOM_SIDE, 20 + MINOR_ARROW_TOP_SIDE)}
    ${drawRing(-1)}
    ${drawRing(0)}
    ${drawRing(1)}
    ${leftOpenRing}
    ${rightOpenRing}
  `;

  return wrapNodeIconInSvgTag(icon, ICON_SIZE, ICON_SIZE, 'topLeft', opts);
}

export const wlcIcon: NodeTypeIcon = createSquareNodeTypeIcon(
  ICON_SIZE,
  createWlcIcon,
);
